/**
 * Per-report feature gates for Reporting V2 (bugfix pass, 2026-07-02).
 *
 * DATE BASIS SELECTOR (CC-49326 → CC-49578) — bug #9 (QA 7/2) shipped the
 * selector config-gated OFF for every report, because the only 2+ date-range
 * report then in the catalog was "Fees Received For Top Clients" and the
 * generated/issued/paid scenario didn't exist yet. CC-49578 turns the feature
 * on for real reports without hardcoding names: while `dateBasisAuto` is true,
 * any definition carrying 2+ filterable DATE columns gets the selector (the
 * panel itself still hides it for 0–1 date columns — slice-5 behavior for
 * single-date reports is unchanged). The explicit allowlists below remain as
 * per-report overrides and as the escape hatch if `dateBasisAuto` is flipped
 * back off (matched by actionKey, then case-insensitive report name — generic
 * reports carry actionKey: "").
 */

// CC-49578: automatic enablement for any report with 2+ date-range filters.
// Set to false to fall back to the explicit allowlists only (bug #9 mode).
export const dateBasisAuto = true

const DATE_BASIS_ACTION_KEYS = new Set([
  // 'cbSomeInvoiceReportView',
])
const DATE_BASIS_REPORT_NAMES = new Set([
  // 'invoices by date', // ← lowercase report name
])

// Date-range filters the panel would actually render — mirrors
// ReportFilterPanel's filterable-column rules (filterable + not declared
// irrelevant for this report per CC-49321).
const filterableDateCount = (definition) =>
  (definition?.columns || []).filter(
    (c) => c.filterable && c.filterTypeName === 'DATE' && c.filterRelevant !== false
  ).length

export const dateBasisEnabled = (definition) => {
  if (!definition) return false
  if (definition.actionKey && DATE_BASIS_ACTION_KEYS.has(definition.actionKey)) return true
  if (DATE_BASIS_REPORT_NAMES.has(String(definition.name || '').trim().toLowerCase())) return true
  return dateBasisAuto && filterableDateCount(definition) >= 2
}

/**
 * RUNNING-TOTAL DATE DEFAULTS (CC-49840) — WIP is a live running total, but CB's
 * own definitions default these reports' date RANGE to a period ("This Month" on
 * the WIP Report family, "This Year" on Aged WIP Expense). Opening the report
 * therefore shows a period-scoped figure where users expect the running total —
 * on vladdemo the This Month default yields 0 rows. Verified 2026-07-27 that V1
 * behaves the same way, so this is CB behavior rather than a V2 regression.
 *
 * Product decision (Jordan, 7/27): deliberately DIVERGE from V1's default and
 * open these reports at All Dates so WIP reads as a true running total. The date
 * filter itself stays fully functional — users can still scope it, and a saved
 * or persisted selection always wins (this only supplies the INITIAL value, and
 * Reset returns here).
 *
 * Matched by report NAME, not id: generic report ids come from each tenant's
 * `cb_Reports` rows and are not portable (the same reason dateBasisEnabled
 * matches on actionKey/name). Reports with no date-range filter are unaffected,
 * so this is a no-op for e.g. WIP–AR–Trust and Aged WIP by Matter.
 * SIMPLEDATE "As Of Date" columns are intentionally left alone — they are the
 * aging anchor, and CB's procs throw on an empty single date.
 */
const RUNNING_TOTAL_NAME_RE = /\bWIP\b/i
// Escape hatch: a WIP-named report that SHOULD open period-scoped (lowercase).
const RUNNING_TOTAL_EXCLUDE_NAMES = new Set([
  // 'wip written off by month',
])

export const datesDefaultToAllDates = (definition) => {
  if (!definition) return false
  const name = String(definition.name || '').trim()
  if (RUNNING_TOTAL_EXCLUDE_NAMES.has(name.toLowerCase())) return false
  return RUNNING_TOTAL_NAME_RE.test(name)
}
